import { formatMoney, type BranchPrice, type Translate } from '@/entities/moderation';
import { useBranchName } from '../../model/use-branch-name';

/** What a branch row charges: its effective price, else the bare override. */
function branchPrice(row: BranchPrice): number | undefined {
  if (typeof row.price === 'number' && Number.isFinite(row.price)) return row.price;
  if (typeof row.own_price === 'number' && Number.isFinite(row.own_price)) {
    return row.own_price;
  }
  return undefined;
}

/**
 * Per-branch prices of one version (`restaurant_products`) — what makes a
 * branch price patch reviewable, since the dish's own `price` may not move.
 * Proposed prices that differ from the live branch row highlight.
 */
export function BranchPricesSection({
  rows,
  liveRows,
  highlight,
  t,
}: {
  rows: BranchPrice[];
  liveRows: BranchPrice[];
  highlight: boolean;
  t: Translate;
}) {
  const visible = rows.filter((row) => (row.restaurant_id?.trim() ?? '') !== '');
  if (visible.length === 0) return null;
  const liveByBranch = new Map(
    liveRows.map((row) => [row.restaurant_id?.trim() ?? '', branchPrice(row)]),
  );
  return (
    <div>
      <h4 className="text-sm leading-[normal] font-semibold text-foreground">
        {t('review.branchPrices', 'Цены в филиалах')}
      </h4>
      <dl className="mt-3 flex flex-col gap-2 text-sm">
        {visible.map((row) => {
          const id = row.restaurant_id?.trim() ?? '';
          const price = branchPrice(row);
          const changed = highlight && liveByBranch.get(id) !== price;
          return (
            <BranchPriceRow
              key={id}
              restaurantId={id}
              price={price}
              ownPrice={typeof row.own_price === 'number'}
              changed={changed}
              t={t}
            />
          );
        })}
      </dl>
    </div>
  );
}

function BranchPriceRow({
  restaurantId,
  price,
  ownPrice,
  changed,
  t,
}: {
  restaurantId: string;
  price: number | undefined;
  ownPrice: boolean;
  changed: boolean;
  t: Translate;
}) {
  const name = useBranchName(restaurantId);
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-3">
      <dt className="min-w-0 truncate text-muted-foreground">{name}</dt>
      <dd
        className={
          changed ? 'text-destructive tabular-nums' : 'text-foreground tabular-nums'
        }
        title={
          ownPrice
            ? t('review.ownPrice', 'Собственная цена филиала')
            : t('review.inheritedPrice', 'Цена товара')
        }
      >
        {formatMoney(price)}
      </dd>
    </div>
  );
}
